import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router-dom';
import { find } from 'lodash';

import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import useStaticProductCardData from 'hooks/useStaticProductCardData';
import LivestreamActiveVideo from './components/LivestreamActiveVideo';
import LivestreamLatestVideos from './components/LivestreamLatestVideos';
import LiveVideoPlayingBanner from './components/LiveVideoPlayingBanner';

const LivestreamPageUI = ({
  isAdmin = false,
  videos,
  videoCategories = [],
  videoStreamingLive,
  loadMoreVideos,
  hasMoreVideos,
}) => {
  const [selectedVideo, setSelectedVideo] = useState();
  const wrapperRef = useRef(null);
  const { slug } = useParams();

  const productIds = useMemo(
    () =>
      videos
        ?.map(video => video?.skuBuilder?.map(product => product?.id))
        ?.flat(1),
    [videos]
  );
  const { products: allProducts } = useStaticProductCardData({ productIds });

  const productsForSelectedVideo = useMemo(() => {
    const productIdsForSelectedVideo = selectedVideo?.skuBuilder.map(
      product => product?.id
    );
    const finalProducts = allProducts?.filter(({ id }) =>
      productIdsForSelectedVideo?.includes(id)
    );
    return finalProducts;
  }, [allProducts, selectedVideo?.skuBuilder]);

  useEffect(() => {
    if (videos) {
      if (slug) {
        const currentVideo = find(videos, { slug });
        setSelectedVideo(currentVideo);
      } else {
        setSelectedVideo(videos[0]);
      }
    }
  }, [slug, videos]);

  return (
    <div
      className="[&_.infinite-scroll-component]:!overflow-x-hidden"
      ref={wrapperRef}
    >
      {!!selectedVideo && (
        <FixedWidthContainer className="!pt-14" noMobilePadding noTabletPadding>
          <LivestreamActiveVideo
            category={selectedVideo.category}
            description={selectedVideo.description}
            isPlayingLive={selectedVideo.isPlayingLive}
            products={productsForSelectedVideo}
            title={selectedVideo.title}
            videoId={selectedVideo.videoId}
            videoType={selectedVideo.videoType}
            isAdmin={isAdmin}
          />
        </FixedWidthContainer>
      )}

      <FixedWidthContainer>
        {!!videos && (
          <InfiniteScroll
            dataLength={videos ? videos.length : 0}
            next={loadMoreVideos}
            hasMore={hasMoreVideos}
            key={`${videos.length}-video-grid`}
            loader={<div>Loading...</div>}
            scrollThreshold={0.53}
          >
            <LivestreamLatestVideos
              selectedVideo={
                selectedVideo
                  ? {
                      videoId: selectedVideo.videoId,
                      videoType: selectedVideo.videoType,
                    }
                  : null
              }
              videoCategories={videoCategories}
              videos={videos.map(video => ({
                ...video,
                isSelected:
                  selectedVideo &&
                  selectedVideo.videoType === video.videoType &&
                  selectedVideo.videoId === video.videoId,
              }))}
            />
          </InfiniteScroll>
        )}
      </FixedWidthContainer>

      {!!videoStreamingLive &&
        selectedVideo &&
        videoStreamingLive.videoId !== selectedVideo.videoId &&
        selectedVideo.videoId !== 'vimeo' && (
          <LiveVideoPlayingBanner
            videoStreamingLive={videoStreamingLive}
            category={videoStreamingLive.category}
            title={videoStreamingLive.title}
            slug={videoStreamingLive?.slugs}
          />
        )}
    </div>
  );
};

LivestreamPageUI.propTypes = {
  isAdmin: PropTypes.bool,
  videoCategories: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      id: PropTypes.string,
    })
  ),
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      categories: PropTypes.array,
      image: PropTypes.string,
      isRecordedLive: PropTypes.bool,
      products: PropTypes.arrayOf(PropTypes.number),
      title: PropTypes.string,
      videoId: PropTypes.string,
      videoLength: PropTypes.string,
      videoType: PropTypes.oneOf(['vimeo', 'wistia']),
    })
  ),
  videoStreamingLive: PropTypes.shape({
    category: PropTypes.string,
    description: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
    videoId: PropTypes.string,
    slugs: PropTypes.string,
  }),
  loadMoreVideos: PropTypes.func,
  hasMoreVideos: PropTypes.bool,
};

export default LivestreamPageUI;
